"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { ArrowUpRight, Trophy, X } from "lucide-react";
import { CAMPANA_SKYTRAX, campanaSkytraxVigente } from "@/lib/config";

/**
 * Banda de campaña de la encuesta de Skytrax. Vive por encima del topbar y, a
 * diferencia de él, existe en todos los anchos y no se retrae con el scroll:
 * el topbar es `hidden lg:block` y se colapsa a los 24px, así que un aviso
 * alojado ahí no lo vería nadie en teléfono ni después del primer gesto.
 *
 * Al pasar `CAMPANA_SKYTRAX.hasta` desaparece sola y no queda nada que
 * revertir: no toca el espaciado de ninguna página, sólo suma alto al header.
 *
 * El cierre no se persiste a propósito: la X aparta la banda mientras se
 * navega, y cualquier recarga la devuelve. Guardarlo dejaba fuera de la
 * campaña, para siempre, a quien la cerrase el primer día.
 */

/**
 * La vigencia se resuelve tras montar, nunca en el render del servidor: en el
 * build quedaría congelado el resultado de comparar contra la fecha de
 * compilación, y la banda sobreviviría a su propia campaña hasta el siguiente
 * despliegue.
 */
function useCampanaVigente() {
  const [vigente, setVigente] = useState(false);
  useEffect(() => setVigente(campanaSkytraxVigente()), []);
  return vigente;
}

export default function SkytraxBanner() {
  const t = useTranslations("votoSkytrax");
  const vigente = useCampanaVigente();
  const [cerrado, setCerrado] = useState(false);

  if (!vigente || cerrado) return null;

  return (
    <aside
      aria-label={t("titulo")}
      className="bg-[var(--botonesSecundarios-iconoLlamativo)] text-[var(--fondoGeneral2)]"
    >
      <div className="w-[92%] max-w-7xl mx-auto py-3 lg:py-0 lg:h-[52px] flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
        <div className="flex items-start lg:items-center gap-2.5 lg:gap-3 min-w-0">
          <Trophy aria-hidden className="size-5 shrink-0 mt-0.5 lg:mt-0" />
          <p className="flex-1 min-w-0 lg:flex lg:items-center lg:gap-2.5">
            <strong className="text-[13px] lg:text-sm font-bold leading-snug">
              {t("titulo")}
            </strong>
            {/* El origen de la encuesta no es adorno: sin él, una banda que
                manda a un dominio externo desconocido se lee como spam. */}
            <span className="block lg:inline mt-0.5 lg:mt-0 text-[11px] lg:text-xs font-medium text-[var(--fondoGeneral2)]/75 lg:whitespace-nowrap">
              {t("fuente")}
            </span>
          </p>
          {/* En teléfono el cierre acompaña al texto; el botón de voto se queda
              con todo el ancho de la fila siguiente. */}
          <button
            type="button"
            onClick={() => setCerrado(true)}
            aria-label={t("cerrar")}
            className="lg:hidden shrink-0 -mt-1 -mr-1 size-9 flex items-center justify-center rounded-full hover:bg-[var(--fondoGeneral2)]/10 transition-colors cursor-pointer"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex items-center gap-3 lg:ml-auto shrink-0">
          <a
            href={CAMPANA_SKYTRAX.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 lg:flex-none inline-flex items-center justify-center gap-2 h-11 lg:h-[38px] px-5 rounded-full bg-[var(--fondoGeneral-secciones)] text-[var(--blanco)] text-sm lg:text-[13px] font-semibold tracking-wide hover:bg-[var(--main-bg)] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--fondoGeneral2)] transition-colors"
          >
            {t("boton")}
            <ArrowUpRight aria-hidden className="size-4" />
          </a>
          <button
            type="button"
            onClick={() => setCerrado(true)}
            aria-label={t("cerrar")}
            className="hidden lg:flex shrink-0 size-7 items-center justify-center rounded-full bg-[var(--fondoGeneral2)]/10 hover:bg-[var(--fondoGeneral2)]/20 transition-colors cursor-pointer"
          >
            <X className="size-3" />
          </button>
        </div>
      </div>
    </aside>
  );
}

/**
 * La misma campaña dentro del drawer, que es donde vive el contenido del
 * topbar por debajo de lg. No mira el cierre de la banda a propósito: una
 * entrada de menú no interrumpe a nadie, y quien cerró el aviso puede seguir
 * queriendo votar más tarde.
 */
export function SkytraxDrawerLink({ onNavigate }: { onNavigate: () => void }) {
  const t = useTranslations("votoSkytrax");
  const vigente = useCampanaVigente();

  if (!vigente) return null;

  return (
    <div className="mt-8 pt-6 border-t border-white/10">
      <a
        href={CAMPANA_SKYTRAX.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        className="flex items-center gap-3 px-4 py-4 rounded-xl bg-[var(--botonesSecundarios-iconoLlamativo)] text-[var(--fondoGeneral2)] hover:brightness-95 transition-all"
      >
        <Trophy aria-hidden className="size-5 shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold leading-snug">
            {t("titulo")}
          </span>
          <span className="block mt-0.5 text-[11px] font-medium text-[var(--fondoGeneral2)]/75">
            {t("fuente")}
          </span>
        </span>
        <ArrowUpRight aria-hidden className="size-4 shrink-0" />
      </a>
    </div>
  );
}
